import React, { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Radar } from "lucide-react";
import { toast } from "sonner";
import { ledgerRefresh } from "@/lib/apiClient";

const hasLedger = (inv) => ["xero", "freshbooks", "zoho", "sage"].some((p) => inv?.[p]?.invoice_id || inv?.[p]?.bill_id);

const SKIP_TEXT = { reauth_required: "needs reconnecting", disconnected: "not connected" };

export const LedgerLiveCheck = ({ inv, onRefreshed }) => {
  const [state, setState] = useState(null); // null | "checking" | result
  const doneFor = useRef(null);
  const cb = useRef(onRefreshed);
  cb.current = onRefreshed;

  const run = async (manual = false) => {
    setState("checking");
    try {
      const r = await ledgerRefresh(inv.id);
      setState(r);
      if (r.checked.length) await cb.current?.();
      if (r.paid) toast.success(`Marked paid in D20 — payment found in ${r.checked.filter((c) => c.paid).map((c) => c.label).join(", ")}`);
      else if (manual) toast.message(r.checked.length ? `Checked ${r.checked.map((c) => c.label).join(", ")} — no changes` : "No connected ledger to check");
    } catch {
      setState({ checked: [], skipped: [], error: true });
    }
  };

  useEffect(() => {
    if (!inv?.id || !hasLedger(inv) || doneFor.current === inv.id) return;
    doneFor.current = inv.id;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv?.id]);

  if (!hasLedger(inv)) return null;
  const checking = state === "checking";
  const r = checking || !state ? null : state;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground" data-testid="ledger-live-check">
      {checking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Radar className="h-3 w-3" />}
      {checking && <span data-testid="ledger-live-checking">Checking the ledger for payment changes…</span>}
      {r && r.error && <span className="text-amber-500">Live check failed — showing last known status</span>}
      {r && !r.error && r.checked.length > 0 && (
        <span data-testid="ledger-live-result">
          Live status from {r.checked.map((c) => c.label).join(", ")} · {new Date(r.checked_at).toLocaleTimeString()}
        </span>
      )}
      {r && !r.error && !r.checked.length && r.skipped.length > 0 && <span>Live check unavailable</span>}
      {r && r.skipped?.map((s) => (
        <span key={s.provider} className="text-amber-500" data-testid={`ledger-live-skipped-${s.provider}`}>
          {s.label} {SKIP_TEXT[s.reason] || s.reason}
        </span>
      ))}
      {!checking && (
        <button type="button" onClick={() => run(true)} className="inline-flex items-center gap-1 underline-offset-2 hover:underline" data-testid="ledger-live-recheck-btn">
          <RefreshCw className="h-3 w-3" /> Check again
        </button>
      )}
    </div>
  );
};
